import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { mat } from "./WorldModels";

const CAR_ROOT = "/assets/kenney-cars";

/** Soft warm tint over Kenney car colormaps. */
const CAR_TINT = 0xf5ebe0;

export interface CarPart {
  geometry: THREE.BufferGeometry;
  material: THREE.MeshLambertMaterial;
  matrix: THREE.Matrix4;
}

export interface CarTemplate {
  id: string;
  parts: CarPart[];
  length: number;
}

const CAR_FILES = [
  "sedan.glb",
  "sedan-sports.glb",
  "hatchback-sports.glb",
  "suv.glb",
  "suv-luxury.glb",
  "van.glb",
  "taxi.glb",
  "truck.glb",
  "delivery.glb",
  "police.glb",
  "ambulance.glb",
  "garbage-truck.glb",
  "firetruck.glb",
] as const;

let ready = false;
let loadPromise: Promise<void> | null = null;
const catalog: CarTemplate[] = [];
const mappedMatCache = new Map<string, THREE.MeshLambertMaterial>();

function matMapped(color: number, map: THREE.Texture | null): THREE.MeshLambertMaterial {
  if (!map) {
    return mat(color);
  }
  const key = `${color}:${map.uuid}`;
  let m = mappedMatCache.get(key);
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color, map });
    m.userData.shared = true;
    mappedMatCache.set(key, m);
  }
  return m;
}

export function carTemplates(): readonly CarTemplate[] {
  return catalog;
}

export function loadCarAssets(): Promise<void> {
  if (ready) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = (async () => {
    const loader = new GLTFLoader();
    await Promise.all(CAR_FILES.map((f) => loadOne(loader, f)));
    ready = true;
  })();
  return loadPromise;
}

async function loadOne(loader: GLTFLoader, file: string): Promise<void> {
  try {
    const gltf = await loader.loadAsync(`${CAR_ROOT}/${file}`);
    const root = gltf.scene;
    root.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(root);
    if (Number.isFinite(box.min.y)) {
      root.position.y -= box.min.y;
      root.updateMatrixWorld(true);
    }

    // Kenney cars face +Z. Three.js Yaw +π/2 maps +Z → +X (nose along +X for traffic).
    // (−π/2 would map +Z → −X and cars drive backwards.)
    root.rotation.y = Math.PI / 2;
    root.updateMatrixWorld(true);

    const sized = new THREE.Box3().setFromObject(root);
    const len = Math.max(0.2, sized.max.x - sized.min.x);
    // Local length ~1.8 so HorizonDecor scale stays familiar.
    const fit = 1.85 / len;
    root.scale.setScalar(fit);
    root.updateMatrixWorld(true);
    const fitted = new THREE.Box3().setFromObject(root);

    const parts: CarPart[] = [];
    root.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh) || !obj.geometry) {
        return;
      }
      const srcMat = Array.isArray(obj.material) ? obj.material[0] : obj.material;
      const map =
        srcMat && "map" in srcMat
          ? ((srcMat as THREE.MeshStandardMaterial).map ?? null)
          : null;
      if (map) {
        map.colorSpace = THREE.SRGBColorSpace;
        map.needsUpdate = true;
      }
      const geometry = obj.geometry.clone();
      geometry.userData.shared = true;
      parts.push({
        geometry,
        material: matMapped(map ? CAR_TINT : 0xb0bec5, map),
        matrix: obj.matrixWorld.clone(),
      });
    });

    if (parts.length === 0) {
      return;
    }

    catalog.push({
      id: file.replace(/\.glb$/i, ""),
      parts,
      length: Math.max(0.5, fitted.max.x - fitted.min.x),
    });
  } catch (err) {
    console.warn(`[cars] failed to load ${file}`, err);
  }
}

/** Clone a Kenney car (feet at y=0, nose toward +X). */
export function cloneCar(variant = 0): THREE.Group | null {
  if (catalog.length === 0) {
    return null;
  }
  const template = catalog[((variant % catalog.length) + catalog.length) % catalog.length]!;
  const group = new THREE.Group();
  group.name = `car-${template.id}`;
  group.userData.carId = template.id;
  for (const part of template.parts) {
    const mesh = new THREE.Mesh(part.geometry, part.material);
    mesh.matrix.copy(part.matrix);
    mesh.matrixAutoUpdate = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    group.add(mesh);
  }
  return group;
}
